/**
 * @file CategoriesTable.tsx
 *
 * List-view table for categories: an optional image, the name (indented by tree
 * depth), description, slug, product count, and per-row edit/delete actions.
 * Which columns render is decided by the parent (responsive flags) and passed in.
 * Presentational: selection and persistence stay in {@link CategoriesView}.
 */

import { Icon } from '@group.one/react-gravity';
import { FormattedMessage, useIntl } from 'react-intl';
import { adminMessages } from '../i18n/messages';
import { ImageOrDefault } from '../components/ImageOrDefault';
import type { CategoryRow } from './category-list';
import styles from './CategoriesView.module.css';

interface CategoriesTableProps {
  rows: CategoryRow[];
  showImage: boolean;
  showDescription: boolean;
  showSlug: boolean;
  onEdit: (category: CategoryRow['category']) => void;
  onDelete: (category: CategoryRow['category']) => void;
}

/**
 * Renders the categories list table.
 *
 * @param props - The page rows, which columns to show, and the row handlers.
 *
 * @returns The table element.
 */
export function CategoriesTable({
  rows,
  showImage,
  showDescription,
  showSlug,
  onEdit,
  onDelete,
}: CategoriesTableProps) {
  const intl = useIntl();

  // Column order is [image?] name [description?] [slug?] count actions, so the
  // count/actions positions shift with the responsive columns; shrink the
  // image/actions columns and right-align the count, mirroring WSB-v2.
  const countIndex =
    (showImage ? 1 : 0) + 1 + (showDescription ? 1 : 0) + (showSlug ? 1 : 0) + 1;
  const columnClass = [
    showImage && 'gv-col-1-shrink',
    `gv-col-${countIndex}-text-right`,
    `gv-col-${countIndex + 1}-shrink`,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <table className={columnClass} data-testid="categories-table">
      <thead>
        <tr>
          {showImage && <th aria-hidden="true" />}
          <th scope="col">
            <FormattedMessage {...adminMessages.categoriesColName} />
          </th>
          {showDescription && (
            <th scope="col">
              <FormattedMessage {...adminMessages.categoriesColDescription} />
            </th>
          )}
          {showSlug && (
            <th scope="col">
              <FormattedMessage {...adminMessages.categoriesColSlug} />
            </th>
          )}
          <th scope="col">
            <FormattedMessage {...adminMessages.categoriesColCount} />
          </th>
          <th scope="col" aria-hidden="true" />
        </tr>
      </thead>
      <tbody>
        {rows.map(({ category, depth }) => (
          <tr key={category.id} data-testid="category-row" data-id={category.id}>
            {showImage && (
              <td>
                <ImageOrDefault
                  src={category.image?.src}
                  alt={category.name}
                  className="gv-thumbnail-image"
                />
              </td>
            )}
            <td>
              <span
                className="gv-text-sm"
                style={
                  depth > 0
                    ? { paddingInlineStart: `calc(var(--size-lg) * ${depth})` }
                    : undefined
                }
              >
                {category.name}
              </span>
            </td>
            {showDescription && (
              <td>
                <span className={`gv-text-sm ${styles.truncate}`}>{category.description}</span>
              </td>
            )}
            {showSlug && (
              <td>
                <span className="gv-text-sm">/{category.slug}</span>
              </td>
            )}
            <td>
              <span className="gv-text-sm">{category.count}</span>
            </td>
            <td>
              <div className={styles.rowActions}>
                <button
                  className="gv-button gv-button-icon"
                  type="button"
                  data-testid="category-row-edit"
                  aria-label={intl.formatMessage(adminMessages.categoriesEdit)}
                  onClick={() => onEdit(category)}
                >
                  <Icon name="edit" size="small" />
                </button>
                <button
                  className="gv-button gv-button-icon"
                  type="button"
                  data-testid="category-row-delete"
                  aria-label={intl.formatMessage(adminMessages.categoriesDelete)}
                  onClick={() => onDelete(category)}
                >
                  <Icon name="delete" size="small" />
                </button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
